import { useState, useEffect } from 'react';
import { Button, Checkbox, Select, NumberInput, TextInput, Text } from '@mantine/core';
import apiService from '../services/api';
import { localizedField } from '../utils/localizedField';
import './AddEditCouponModal.css';

const getDefaultDateTime = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function AddEditCouponModal({ coupon, cloneData, onClose, onSaved }) {
  const isClone = !!cloneData;
  const source = cloneData || coupon;

  const buildInitialForm = () => {
    if (!source) {
      return {
        code: '',
        rule_type: 'percentage',
        value: '',
        description: '',
        applies_to: 'product',
        product_sku: '',
        category: '',
        start_time: getDefaultDateTime(),
        end_time: getDefaultDateTime(),
        max_uses: 1,
        active: true,
        business_only: false
      };
    }

    const description = Array.isArray(source.translations) && source.translations.length > 0
      ? (source.translations.find(t => t.language_code === 'en') || source.translations[0]).description || ''
      : source.description || '';

    let applies_to = 'all';
    if (source.product_sku) applies_to = 'product';
    else if (source.category) applies_to = 'category';

    return {
      code: isClone ? '' : (source.code || ''),
      rule_type: source.rule_type || 'percentage',
      value: source.value ?? '',
      description,
      applies_to,
      product_sku: source.product_sku || '',
      category: source.category || '',
      start_time: source.start_time?.slice(0, 16) || getDefaultDateTime(),
      end_time: source.end_time?.slice(0, 16) || getDefaultDateTime(),
      max_uses: source.max_uses ?? 1,
      active: source.active ?? true,
      business_only: !!source.business_only
    };
  };

  const [form, setForm] = useState(buildInitialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    apiService.getProductCategories().then(setCategories).catch(() => {});
    apiService.getProducts({ include_hidden: true }).then(setProducts).catch(() => {});
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (form.applies_to === 'product' && !form.product_sku) {
      setError('Product SKU is required when applies to Product.');
      setLoading(false);
      return;
    }
    if (form.applies_to === 'category' && !form.category) {
      setError('Category is required when applies to Category.');
      setLoading(false);
      return;
    }

    const payload = {
      ...form,
      rule_type: form.rule_type,
    };
    if (form.product_sku) {
      const variant = products.flatMap(p => p.variants || []).find(v => v.sku === form.product_sku);
      if (variant) payload.variant_id = variant.uuid;
    }

    try {
      if (coupon && !isClone) {
        await apiService.updateCoupon(coupon.id, payload);
      } else {
        await apiService.addCoupon(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const allVariants = products.flatMap(p => (p.variants || []).map(v => ({
    value: v.sku,
    label: `${v.sku} — ${localizedField(p, 'name', 'en') || v.sku}`,
  })));
  const categoryOptions = categories.map(c => ({ value: c.name, label: c.name }));

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="add-edit-coupon-title">
      <h2 id="add-edit-coupon-title">{isClone ? 'Clone Coupon' : coupon ? 'Edit Coupon' : 'Add Coupon'}</h2>
      {error ? <div className="modal-error" role="alert">{error}</div> : null}
      <form onSubmit={handleSave} noValidate>
        <div className="form-group">
          <label htmlFor="code">Coupon Code *</label>
          <TextInput
            id="code"
            name="code"
            value={form.code}
            onChange={handleChange}
            placeholder="e.g. SUMMER10"
            required
          />
          {isClone && (
            <Text size="xs" c="dimmed" mt={4}>Enter a new unique code for the cloned coupon.</Text>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="rule_type">Discount Type *</label>
          <Select
            id="rule_type"
            data={[
              { value: 'percentage', label: 'Percentage' },
              { value: 'fixed', label: 'Fixed Amount (CHF)' },
            ]}
            value={form.rule_type}
            onChange={(val) => setForm(f => ({ ...f, rule_type: val || 'percentage' }))}
          />
        </div>

        <div className="form-group">
          <label htmlFor="value">{form.rule_type === 'fixed' ? 'Discount Amount (CHF) *' : 'Discount Percentage (%) *'}</label>
          <NumberInput
            id="value"
            value={form.value}
            onChange={(val) => setForm(f => ({ ...f, value: val }))}
            min={0}
            max={form.rule_type === 'percentage' ? 100 : undefined}
            step={0.01}
            required
            hideControls
          />
        </div>

        <div className="form-group">
          <label htmlFor="applies_to">Applies To *</label>
          <Select
            id="applies_to"
            data={[
              { value: 'product', label: 'Product' },
              { value: 'category', label: 'Category' },
              { value: 'all', label: 'All Products' },
            ]}
            value={form.applies_to}
            onChange={(val) => setForm(f => ({ ...f, applies_to: val || 'product' }))}
          />
        </div>

        {form.applies_to === 'product' && (
          <div className="form-group">
            <label htmlFor="product_sku">Product SKU *</label>
            <Select
              id="product_sku"
              data={allVariants}
              value={form.product_sku || null}
              onChange={(val) => setForm(f => ({ ...f, product_sku: val || '' }))}
              searchable
              clearable
              placeholder="Select product"
            />
          </div>
        )}
        {form.applies_to === 'category' && (
          <div className="form-group">
            <label htmlFor="category">Category *</label>
            <Select
              id="category"
              data={categoryOptions}
              value={form.category || null}
              onChange={(val) => setForm(f => ({ ...f, category: val || '' }))}
              searchable
              clearable
              placeholder="Select category"
            />
          </div>
        )}

        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="start_time">Start Time *</label>
            <input id="start_time" name="start_time" type="datetime-local" value={form.start_time} onChange={handleChange} required />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="end_time">End Time *</label>
            <input id="end_time" name="end_time" type="datetime-local" value={form.end_time} onChange={handleChange} required />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="max_uses">Max Uses *</label>
          <NumberInput
            id="max_uses"
            value={form.max_uses}
            onChange={(val) => setForm(f => ({ ...f, max_uses: val }))}
            min={1}
            step={1}
            required
            hideControls
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <TextInput
            id="description"
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="e.g. Welcome discount for new customers"
          />
        </div>

        <Checkbox
          name="active"
          label="Active"
          checked={form.active}
          onChange={(e) => setForm(f => ({ ...f, active: e.currentTarget.checked }))}
        />

        <Checkbox
          name="business_only"
          label="For Business Customers Only"
          checked={!!form.business_only}
          onChange={(e) => setForm(f => ({ ...f, business_only: e.currentTarget.checked }))}
        />

        <div className="btns-container">
          <Button type="submit" loading={loading}>Save</Button>
          <Button variant="subtle" color="gray" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
